import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { contactCustomFieldsApi } from "../../../api/contactCustomFieldsApi";
import styles from "../CRMSettingsPage.module.css";

const FIELD_TYPES = [
    ["text", "field_type_text"],
    ["number", "field_type_number"],
    ["date", "field_type_date"],
    ["boolean", "field_type_boolean"],
    ["select", "field_type_select"],
];

const emptyForm = {
    label: "",
    key: "",
    data_type: "text",
    is_required: false,
    is_searchable: true,
    is_visible_in_table: true,
    is_active: true,
    options: "",
    default_value: "",
};

function getErrorMessage(error, fallback = "Something went wrong") {
    const detail = error?.response?.data?.detail;

    if (Array.isArray(detail)) {
        return detail
            .map((item) => item?.msg || JSON.stringify(item))
            .join(", ");
    }

    if (detail && typeof detail === "object") {
        return JSON.stringify(detail);
    }

    return detail || error?.message || fallback;
}

export default function ContactCustomFieldsTab() {
    const { t } = useTranslation();

    const getFieldLabel = (field) => {
        const labelKey = field?.label || field?.label_key || field?.key || "";
        const fallback =
            field?.options_json?.fallback_label ||
            field?.fallback_label ||
            field?.key ||
            labelKey;

        return t(labelKey, {
            defaultValue: fallback,
        });
    };

    const [fields, setFields] = useState([]);
    const [form, setForm] = useState(emptyForm);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const loadFields = async () => {
        try {
            setLoading(true);
            setError("");

            const data = await contactCustomFieldsApi.list();
            setFields(Array.isArray(data) ? data : []);
        } catch (e) {
            setError(
                getErrorMessage(
                    e,
                    t("contact_custom_field_error_load", {
                        defaultValue: "Failed to load custom fields",
                    })
                )
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadFields();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateForm = (key, value) => {
        if (key === "label") {
            const generatedKey = value
                .trim()
                .toLowerCase()
                .replace(/[^a-z0-9]+/gi, "_")
                .replace(/^_+|_+$/g, "");

            setForm((prev) => ({
                ...prev,
                label: value,
                key: prev.key || generatedKey,
            }));

            return;
        }

        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const handleCreate = async () => {
        if (!form.label.trim()) {
            setError(
                t("contact_custom_field_error_label_required", {
                    defaultValue: "Field label is required",
                })
            );
            return;
        }

        if (!form.key.trim()) {
            setError(
                t("contact_custom_field_error_key_required", {
                    defaultValue: "Field key is required",
                })
            );
            return;
        }

        const payload = {
            entity_type: "contact",
            label: form.label.trim(),
            key: form.key.trim().toLowerCase(),
            data_type: form.data_type,

            is_required: !!form.is_required,
            is_searchable: !!form.is_searchable,
            is_visible_in_table: !!form.is_visible_in_table,
            is_active: !!form.is_active,

            default_value:
                form.default_value !== ""
                    ? form.default_value
                    : null,

            options_json:
                form.data_type === "select"
                    ? {
                        options: form.options
                            .split(",")
                            .map((x) => x.trim())
                            .filter(Boolean),
                    }
                    : null,
        };

        try {
            setSaving(true);
            setError("");

            await contactCustomFieldsApi.create(payload);

            setForm(emptyForm);

            await loadFields();
        } catch (e) {
            setError(
                getErrorMessage(
                    e,
                    t("contact_custom_field_error_create", {
                        defaultValue: "Failed to create custom field",
                    })
                )
            );
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (field) => {
        const translatedLabel = getFieldLabel(field);

        const ok = window.confirm(
            t("contact_custom_field_delete_confirm", {
                label: translatedLabel,
                defaultValue: `Delete field "${translatedLabel}"?`,
            })
        );

        if (!ok) return;

        try {
            setError("");

            await contactCustomFieldsApi.delete(field.id);

            await loadFields();
        } catch (e) {
            setError(
                getErrorMessage(
                    e,
                    t("contact_custom_field_error_delete", {
                        defaultValue: "Failed to delete custom field",
                    })
                )
            );
        }
    };

    return (
        <div className={styles.card}>
            <div className={styles.cardTitle}>
                {t("crm_settings_contact_fields_title", {
                    defaultValue: "Contact custom fields",
                })}
            </div>

            <div className={styles.cardSub}>
                {t("crm_settings_contact_fields_subtitle", {
                    defaultValue:
                        "Create additional fields for contacts without changing code.",
                })}
            </div>

            {error && (
                <div className="alert alert-danger py-2 mt-3">
                    {error}
                </div>
            )}

            <div
                className="mt-4"
                style={{
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: 20,
                    padding: 24,
                    background:
                        "linear-gradient(180deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.01) 100%)",
                }}
            >
                <div className="row g-4 align-items-start">

                    {/* LABEL */}
                    <div className="col-12 col-lg-3">
                        <label className="form-label fw-semibold">
                            {t("contact_custom_field_label", {
                                defaultValue: "Label",
                            })}
                        </label>

                        <input
                            className="form-control"
                            value={form.label}
                            onChange={(e) =>
                                updateForm("label", e.target.value)
                            }
                            placeholder={t("contact_custom_field_label_placeholder", {
                                defaultValue: "Telegram username",
                            })}
                        />

                        <div
                            className="mt-2"
                            style={{
                                fontSize: 12,
                                opacity: 0.7,
                            }}
                        >
                            {t("contact_custom_field_label_hint", {
                                defaultValue: "Displayed name of the field",
                            })}
                        </div>
                    </div>

                    {/* KEY */}
                    <div className="col-12 col-lg-3">
                        <label className="form-label fw-semibold">
                            {t("contact_custom_field_key", {
                                defaultValue: "Key",
                            })}
                        </label>

                        <input
                            className="form-control"
                            value={form.key}
                            onChange={(e) =>
                                updateForm("key", e.target.value)
                            }
                            placeholder={t("contact_custom_field_key_placeholder", {
                                defaultValue: "telegram_username",
                            })}
                        />

                        <div
                            className="mt-2"
                            style={{
                                fontSize: 12,
                                opacity: 0.7,
                            }}
                        >
                            {t("contact_custom_field_key_hint", {
                                defaultValue: "Unique identifier (no spaces)",
                            })}
                        </div>
                    </div>

                    {/* TYPE */}
                    <div className="col-12 col-md-4 col-lg-2">
                        <label className="form-label fw-semibold">
                            {t("contact_custom_field_type", {
                                defaultValue: "Type",
                            })}
                        </label>

                        <select
                            className="form-select"
                            value={form.data_type}
                            onChange={(e) =>
                                updateForm("data_type", e.target.value)
                            }
                        >
                            {FIELD_TYPES.map(([type, labelKey]) => (
                                <option key={type} value={type}>
                                    {t(labelKey, {
                                        defaultValue: type,
                                    })}
                                </option>
                            ))}
                        </select>

                        <div
                            className="mt-2"
                            style={{
                                fontSize: 12,
                                opacity: 0.7,
                            }}
                        >
                            {t("contact_custom_field_type_hint", {
                                defaultValue: "Data type of the field",
                            })}
                        </div>
                    </div>

                    {/* OPTIONS */}
                    {form.data_type === "select" && (
                        <div className="col-12 col-md-8 col-lg-4">
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <label className="form-label fw-semibold mb-0">
                                    {t("contact_custom_field_options", {
                                        defaultValue: "Options",
                                    })}
                                </label>

                                <span
                                    style={{
                                        fontSize: 11,
                                        padding: "4px 8px",
                                        borderRadius: 999,
                                        background: "rgba(34,197,94,0.15)",
                                        color: "#4ade80",
                                        fontWeight: 600,
                                    }}
                                >
                                    {t("contact_custom_field_options_badge", {
                                        defaultValue: "Only for select",
                                    })}
                                </span>
                            </div>

                            <input
                                className="form-control"
                                value={form.options}
                                onChange={(e) =>
                                    updateForm("options", e.target.value)
                                }
                                placeholder={t("contact_custom_field_options_placeholder", {
                                    defaultValue: "VIP, Regular, Partner",
                                })}
                            />

                            <div
                                className="mt-2"
                                style={{
                                    fontSize: 12,
                                    opacity: 0.7,
                                }}
                            >
                                {t("contact_custom_field_options_hint", {
                                    defaultValue: "Values separated by comma",
                                })}
                            </div>
                        </div>
                    )}
                </div>

                <hr
                    style={{
                        borderColor: "rgba(255,255,255,0.08)",
                        margin: "28px 0",
                    }}
                />

                <div className="row g-4 align-items-end">

                    {/* DEFAULT VALUE */}
                    <div className="col-12 col-lg-3">
                        <label className="form-label fw-semibold">
                            {t("contact_custom_field_default_value", {
                                defaultValue: "Default value",
                            })}
                        </label>

                        <input
                            className="form-control"
                            value={form.default_value}
                            onChange={(e) =>
                                updateForm(
                                    "default_value",
                                    e.target.value
                                )
                            }
                            placeholder={t("contact_custom_field_default_value_placeholder", {
                                defaultValue: "Leave empty for none",
                            })}
                        />

                        <div
                            className="mt-2"
                            style={{
                                fontSize: 12,
                                opacity: 0.7,
                            }}
                        >
                            {t("contact_custom_field_default_value_hint", {
                                defaultValue: "Default value for the field",
                            })}
                        </div>
                    </div>

                    {/* SETTINGS */}
                    <div className="col-12 col-lg-6">
                        <label
                            className="form-label fw-semibold d-block"
                            style={{ marginBottom: 14 }}
                        >
                            {t("contact_custom_field_settings", {
                                defaultValue: "Field settings",
                            })}
                        </label>

                        <div className="d-flex flex-wrap gap-4">

                            <label
                                className="form-check d-flex align-items-start gap-2"
                                style={{ minWidth: 140 }}
                            >
                                <input
                                    type="checkbox"
                                    className="form-check-input mt-1"
                                    checked={form.is_required}
                                    onChange={(e) =>
                                        updateForm(
                                            "is_required",
                                            e.target.checked
                                        )
                                    }
                                />

                                <div>
                                    <div className="fw-semibold">
                                        {t("required", {
                                            defaultValue: "Required",
                                        })}
                                    </div>

                                    <div
                                        style={{
                                            fontSize: 12,
                                            opacity: 0.65,
                                        }}
                                    >
                                        {t("contact_custom_field_required_hint", {
                                            defaultValue: "Field must be filled",
                                        })}
                                    </div>
                                </div>
                            </label>

                            <label
                                className="form-check d-flex align-items-start gap-2"
                                style={{ minWidth: 140 }}
                            >
                                <input
                                    type="checkbox"
                                    className="form-check-input mt-1"
                                    checked={form.is_searchable}
                                    onChange={(e) =>
                                        updateForm(
                                            "is_searchable",
                                            e.target.checked
                                        )
                                    }
                                />

                                <div>
                                    <div className="fw-semibold">
                                        {t("contact_custom_field_searchable", {
                                            defaultValue: "Searchable",
                                        })}
                                    </div>

                                    <div
                                        style={{
                                            fontSize: 12,
                                            opacity: 0.65,
                                        }}
                                    >
                                        {t("contact_custom_field_searchable_hint", {
                                            defaultValue: "Available in search",
                                        })}
                                    </div>
                                </div>
                            </label>

                            <label
                                className="form-check d-flex align-items-start gap-2"
                                style={{ minWidth: 160 }}
                            >
                                <input
                                    type="checkbox"
                                    className="form-check-input mt-1"
                                    checked={form.is_visible_in_table}
                                    onChange={(e) =>
                                        updateForm(
                                            "is_visible_in_table",
                                            e.target.checked
                                        )
                                    }
                                />

                                <div>
                                    <div className="fw-semibold">
                                        {t("contact_custom_field_show_in_table", {
                                            defaultValue: "Show in table",
                                        })}
                                    </div>

                                    <div
                                        style={{
                                            fontSize: 12,
                                            opacity: 0.65,
                                        }}
                                    >
                                        {t("contact_custom_field_show_in_table_hint", {
                                            defaultValue: "Visible in contacts table",
                                        })}
                                    </div>
                                </div>
                            </label>
                        </div>
                    </div>

                    {/* BUTTON */}
                    <div className="col-12 col-lg-3">
                        <button
                            type="button"
                            className="btn btn-primary w-100"
                            onClick={handleCreate}
                            disabled={saving}
                            style={{
                                height: 48,
                                borderRadius: 14,
                                fontWeight: 600,
                            }}
                        >
                            {saving
                                ? t("saving", {
                                    defaultValue: "Saving...",
                                })
                                : t("contact_custom_field_create", {
                                    defaultValue: "Create field",
                                })}
                        </button>
                    </div>
                </div>
            </div>

            <div
                className="mt-4"
                style={{
                    border: "1px dashed rgba(255,255,255,0.12)",
                    borderRadius: 18,
                    padding: 24,
                    background: "rgba(255,255,255,0.01)",
                }}
            >
                {loading ? (
                    <p>
                        {t("loading", {
                            defaultValue: "Loading",
                        })}
                        ...
                    </p>
                ) : fields.length === 0 ? (
                    <div
                        style={{
                            opacity: 0.75,
                        }}
                    >
                        <div
                            style={{
                                fontSize: 22,
                                marginBottom: 8,
                            }}
                        >
                            {t("contact_custom_field_empty_title", {
                                defaultValue: "No custom fields yet.",
                            })}
                        </div>

                        <div
                            style={{
                                fontSize: 14,
                                opacity: 0.7,
                            }}
                        >
                            {t("contact_custom_field_empty_text", {
                                defaultValue:
                                    "Create your first custom field using the form above.",
                            })}
                        </div>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-hover align-middle">
                            <thead>
                                <tr>
                                    <th>{t("id", { defaultValue: "ID" })}</th>
                                    <th>
                                        {t("contact_custom_field_column_label", {
                                            defaultValue: "Label",
                                        })}
                                    </th>
                                    <th>
                                        {t("contact_custom_field_column_key", {
                                            defaultValue: "Key",
                                        })}
                                    </th>
                                    <th>
                                        {t("contact_custom_field_column_type", {
                                            defaultValue: "Type",
                                        })}
                                    </th>
                                    <th>
                                        {t("contact_custom_field_column_required", {
                                            defaultValue: "Required",
                                        })}
                                    </th>
                                    <th>
                                        {t("contact_custom_field_column_searchable", {
                                            defaultValue: "Searchable",
                                        })}
                                    </th>
                                    <th>
                                        {t("contact_custom_field_column_in_table", {
                                            defaultValue: "In table",
                                        })}
                                    </th>
                                    <th>
                                        {t("contact_custom_field_column_default", {
                                            defaultValue: "Default",
                                        })}
                                    </th>
                                    <th></th>
                                </tr>
                            </thead>

                            <tbody>
                                {fields.map((field) => (
                                    <tr key={field.id}>
                                        <td>{field.id}</td>
                                        <td>{getFieldLabel(field)}</td>
                                        <td>{field.key}</td>
                                        <td>
                                            {t(`field_type_${field.data_type}`, {
                                                defaultValue: field.data_type,
                                            })}
                                        </td>

                                        <td>
                                            {field.is_required
                                                ? t("yes", {
                                                    defaultValue: "Yes",
                                                })
                                                : t("no", {
                                                    defaultValue: "No",
                                                })}
                                        </td>

                                        <td>
                                            {field.is_searchable
                                                ? t("yes", {
                                                    defaultValue: "Yes",
                                                })
                                                : t("no", {
                                                    defaultValue: "No",
                                                })}
                                        </td>

                                        <td>
                                            {field.is_visible_in_table
                                                ? t("yes", {
                                                    defaultValue: "Yes",
                                                })
                                                : t("no", {
                                                    defaultValue: "No",
                                                })}
                                        </td>

                                        <td>
                                            {field.default_value !== null &&
                                                field.default_value !== undefined &&
                                                field.default_value !== ""
                                                ? field.default_value
                                                : "-"}
                                        </td>

                                        <td className="text-end">
                                            <button
                                                type="button"
                                                className="btn btn-outline-danger btn-sm"
                                                onClick={() =>
                                                    handleDelete(field)
                                                }
                                            >
                                                {t("delete", {
                                                    defaultValue: "Delete",
                                                })}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}